import { FC, Fragment, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { moTable } from 'constants/mo';
import { MoTableData, MyOrder, OrderLog } from 'ts/shared/interfaces/MOResponse';
import MoDetails from './MoDetails';
import { useTranslation } from 'react-i18next';
import MoOrderTabs from './MoOrderTabs2';
import LoadingBlock from '../LoadingBlock';
import { faGear, faHome, faSearch } from '@fortawesome/free-solid-svg-icons';
import MoStatistics from './MoStatistics';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TableTanStack from './TableTanStack';
import MoTableSettingsModal from './MoTableSettingsModal';
import { Column } from 'ts/shared/interfaces/Generic';
import MoLogTable from './MoLogTable';
import MoMode2 from './MoMode2';
import { MainContext } from 'contexts/MainContext';
import Tabs from 'components/ui/Tabs/Tabs';
import OrdersActiveBridge from './Assistant/OrdersActiveBridge';
import {
  OrdersAction,
  actionTargetsOrder,
  getOrdersActionsVersion,
  getOrdersListFilter,
  getOrdersListFilterVersion,
  impliedOrderTab,
  peekOrdersActions,
  subscribeOrdersActions,
  subscribeOrdersListFilter,
  useOrdersAction,
} from './Assistant/ordersActions';
import { openLog } from 'services/myOrders';
import { highlightAssistantTargets } from 'utils/assistantHighlight';

interface MoTabs2Props {
  tableData: MoTableData[];
  searchLoading: boolean;
  onUpdateMyOrder: (row: MyOrder) => void;
  myOrders: MyOrder[];
  onChangeTableData: (data: MoTableData[]) => void;
  activeOrder?: MyOrder;
  setActiveOrder: (order: MyOrder) => void;
}
const MoTabs2: FC<MoTabs2Props> = ({ tableData, activeOrder, setActiveOrder, ...props }) => {
  const { t } = useTranslation();
  const [showSingleTab, setShowSingleTab] = useState(false);
  const [row, setRow] = useState<MoTableData>();
  const visibleData = tableData.filter((row) => row.visible);
  const [globalFilter, setGlobalFilter] = useState('');
  const listFilterVersion = useSyncExternalStore(subscribeOrdersListFilter, getOrdersListFilterVersion);
  const [showSettings, setShowSettings] = useState(false);
  const [orderLogs, setOrderLogs] = useState<OrderLog[]>();
  const [activeTab, setActiveTab] = useState(0);
  const ordersActionVersion = useSyncExternalStore(subscribeOrdersActions, getOrdersActionsVersion);
  const { userSiteConfig } = useContext(MainContext);
  const [moTableState, setMoTableState] = useState<Column[]>(
    localStorage.getItem('moTableState')
      ? JSON.parse(localStorage.getItem('moTableState') || '')
      : moTable
  );

  useEffect(() => {
    const update = getOrdersListFilter();
    if (!listFilterVersion) return;
    setActiveTab(0);
    if (update.clearText) setGlobalFilter(update.text ?? '');
    else if (update.text !== undefined) setGlobalFilter(update.text);
    highlightAssistantTargets(['list']);
  }, [listFilterVersion]);

  useEffect(() => {
    if (!tableData.length) {
      onInit();
    } else {
      (async () => {
        const isRownInTable = tableData.some(
          (row) => row.segmentId === activeOrder?.fullSegment.orderSegId
        );
        if (isRownInTable && activeOrder) {
          setActiveOrder(activeOrder);
        } else if (!activeOrder || !isRownInTable) {
          setActiveOrder(props.myOrders[0]);
        }
      })();
    }
  }, [tableData]); //eslint-disable-line

  const onInit = () => {
    setActiveTab(0);
    setShowSingleTab(false);
  };

  const onChangeActiveRow = (segmentId: number) => {
    const newActiveOrder = props.myOrders.find(
      (order) => order.fullSegment.orderSegId === segmentId
    );

    if (newActiveOrder) {
      sessionStorage.setItem('activeMyOrderNames', JSON.stringify(newActiveOrder?.order.paxes));
      setActiveOrder(newActiveOrder);
    }
  };

  const onRowDoubleClick = (segmentId: number) => {
    const segment = visibleData.find((row) => row.segmentId === segmentId);

    if (segment) {
      setRow(segment);

      setActiveTab(1);
      setShowSingleTab(true);
    }
  };

  const onClickSearchMobile = () => {
    const el = document.querySelector('.form-wrapper.m-search') as HTMLElement;
    if (el) {
      const currentDisplay = window.getComputedStyle(el).display;
      el.style.display = currentDisplay === 'none' ? 'flex' : 'none';
    }
  };

  const onLoadLogs = (logs: OrderLog[]) => {
    setActiveTab(2);
    setOrderLogs(logs);
  };

  const targetsVisibleOrder = (action: OrdersAction) => {
    if (!activeOrder) {
      return !action.segmentId && !action.orderId && !action.targetId && !action.index;
    }
    return actionTargetsOrder(
      action,
      {
        segmentId: activeOrder.orderRow.segmentId,
        altSegmentId: activeOrder.fullSegment.orderSegId,
        orderId: activeOrder.orderRow.orderId,
        altOrderId: activeOrder.fullSegment.orderId,
      },
      true,
    );
  };

  useEffect(() => {
    if (activeTab !== 2) return;
    const pending = peekOrdersActions().some(
      (action) =>
        targetsVisibleOrder(action) &&
        ((action.action === 'tab' && action.tabScope === 'order') || !!impliedOrderTab(action.action)),
    );
    if (pending) setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersActionVersion, activeTab, activeOrder]);

  const openSegmentTab = (order = activeOrder) => {
    if (!order) return;
    const segment = visibleData.find(
      (item) => item.segmentId === order.fullSegment.orderSegId || item.segmentId === order.orderRow.segmentId,
    );
    if (segment) setRow(segment);
    setShowSingleTab(true);
    setActiveTab(1);
    highlightAssistantTargets(['orders-tabs']);
  };

  useOrdersAction(
    (action) => action.action === 'tab' && action.tabScope === 'page' && targetsVisibleOrder(action),
    (action) => {
      if (action.tab === 'orders') {
        setActiveTab(0);
        highlightAssistantTargets(['list']);
        return;
      }
      if (action.tab === 'segment') {
        openSegmentTab();
        return;
      }
      if (action.tab === 'logs') {
        if (orderLogs?.length) {
          setActiveTab(2);
          highlightAssistantTargets(['orders-tabs']);
          return;
        }
        if (!activeOrder) return;
        void openLog({
          orderId: activeOrder.orderRow.orderId,
          segmentId: activeOrder.orderRow.segmentId,
        }).then((res) => {
          if (res?.orderLogs) onLoadLogs(res.orderLogs);
        });
      }
    },
    activeOrder?.fullSegment.orderSegId || 0,
  );

  useOrdersAction(
    (action) => action.action === 'table-settings' && targetsVisibleOrder(action),
    () => {
      setActiveTab(0);
      setShowSettings(true);
      highlightAssistantTargets(['list']);
    },
    activeOrder?.fullSegment.orderSegId || 0,
  );

  useEffect(() => {
    const pendingSelect = peekOrdersActions().some((action) => action.action === 'select');
    if (pendingSelect) setActiveTab(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ordersActionVersion]);

  return (
    <Fragment>
      <OrdersActiveBridge
        orders={props.myOrders}
        activeOrder={activeOrder}
        setActiveOrder={setActiveOrder}
      />
      {userSiteConfig.base.myOrdersMode === 0 && (
        <Fragment>
          <div dir="ltr" className="mo-tabs-wr" data-assistant-target="list">
            {!!tableData.length && (
              <Fragment>
                <div className="display-flex flex-v-center">
                  <Tabs
        size="small"
                    label={t('mo.mo')}
                    value={String(activeTab)}
                    onValueChange={(v) => setActiveTab(Number(v))}
                    items={[
                      {
                        value: '0',
                        label: (
                          <>
                            <FontAwesomeIcon icon={faHome} className="mr-5" />
                            {t('mo.mo')}
                          </>
                        ),
                      },
                      ...(showSingleTab
                        ? [
                            {
                              value: '1',
                              label: `${t('mo.segment_full')}-${row?.segmentId} (${t('mo.order')}-${row?.id})`,
                            },
                          ]
                        : []),
                      ...(orderLogs?.length
                        ? [
                            {
                              value: '2',
                              label: `${t('mo.logs')} ${orderLogs[0].sysSuppCode}`,
                            },
                          ]
                        : []),
                    ]}
                  />
                  <div className="ml-a flex-row" id="mo-statistics">
                    <MoStatistics tableData={visibleData} />
                    <input
                      className="mo-table-filter-input"
                      placeholder="Filter..."
                      value={globalFilter}
                      onChange={(e) => setGlobalFilter(e.target.value)}
                    />
                    <div className="ph-5 cur-poi m-search-mobile" onClick={onClickSearchMobile}>
                      <FontAwesomeIcon icon={faSearch} />
                    </div>
                    <div className="ph-5 cur-poi" onClick={() => setShowSettings(true)}>
                      <FontAwesomeIcon icon={faGear} />
                    </div>
                  </div>
                </div>

                {activeTab === 0 && activeOrder && (
                  <TableTanStack
                    data={visibleData}
                    columns={moTableState.filter((column) => column.visible)}
                    onDoubleClick={onRowDoubleClick}
                    onClick={onChangeActiveRow}
                    activeId={activeOrder?.fullSegment.orderSegId}
                    globalFilter={globalFilter}
                    tableId="my-orders"
                  />
                )}
                {activeTab === 1 && activeOrder && (
                  <MoDetails
                    backToOrders={onInit}
                    onUpdateMyOrder={props.onUpdateMyOrder}
                    myOrder={activeOrder}
                    onLoadLogs={onLoadLogs}
                  />
                )}
                {activeTab === 2 && orderLogs && <MoLogTable data={orderLogs} />}
              </Fragment>
            )}

          </div>
          {!!props.myOrders.length && activeTab === 0 && activeOrder && (
            <MoOrderTabs
              onUpdateMyOrder={props.onUpdateMyOrder}
              activeOrder={activeOrder}
              onLoadLogs={onLoadLogs}
            />
          )}

          <MoTableSettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            onSuccess={() => { }}
            tableState={moTableState}
            onChangeTableState={(state) => setMoTableState(state)}
          />
        </Fragment>
      )}
      {props.searchLoading && !tableData.length && (
        <div className="mo-loader">
          <LoadingBlock />
        </div>
      )}
      {userSiteConfig.base.myOrdersMode !== 0 && (
        <MoMode2 tableData={tableData} myOrders={props.myOrders} onUpdateMyOrder={props.onUpdateMyOrder} />
      )}
    </Fragment>
  );
};

export default MoTabs2;
